import { useEffect, useMemo, useState } from "react";
import "./AdminMembersOccupancyModule.scss";
import { toast } from "react-toastify";
import {
  IUser,
  MemberStatisticsExport,
  OccupancyStatisticsExport,
} from "../../../types/api";
import StatisticsFilterList from "../StatisticsFilterList/StatisticsFilterList";

import { createStatisticsFilterStore } from "../Stores/statisticsFilterStoreFactory";
import { ReportTableSection } from "../Tables/ReportTableSection";
import { MembertypeChart } from "../Charts/MembertypeChart";
import { GuestChart } from "../Charts/GuestChart";
import AdminSpecificMemberModule from "./AdminSpecificMemberModule/AdminSpecificMemberModule";

interface AdminMembersOccupancyModuleProps {
  memberOccupancyData: MemberStatisticsExport;
  memberOccupancyFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  startDate: string;
  endDate: string;
  selectedWeekdays: number[];
  toggleSpecificFetch: boolean;
  user: IUser | undefined;
  setShowChooseUserPopup: (show: boolean) => void;
}

const AdminMembersOccupancyModule = ({
  memberOccupancyData,
  memberOccupancyFilterStore,
  startDate,
  endDate,
  selectedWeekdays,
  toggleSpecificFetch,
  user,
  setShowChooseUserPopup,
}: AdminMembersOccupancyModuleProps) => {
  function countWeekdaysFromIntervals(startDate: Date, endDate: Date) {
    const counts = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    const start = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate()
      )
    );
    const end = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate()
      )
    );

    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      const dayOfWeek = d.getUTCDay(); // 0 = Sunday
      const isoDay = dayOfWeek;

      if (selectedWeekdays.includes(isoDay)) {
        switch (isoDay) {
          case 1:
            counts.monday++;
            break;
          case 2:
            counts.tuesday++;
            break;
          case 3:
            counts.wednesday++;
            break;
          case 4:
            counts.thursday++;
            break;
          case 5:
            counts.friday++;
            break;
          case 6:
            counts.saturday++;
            break;
          case 0:
            counts.sunday++;
            break;
        }
      }
    }

    return counts;
  }

  const [weekdayCounts, setWeekdayCounts] = useState({
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  });

  const totalDays = Object.values(weekdayCounts).reduce(
    (acc, count) => acc + count,
    0
  );

  useEffect(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const counts = countWeekdaysFromIntervals(start, end);
    setWeekdayCounts(counts);
  }, [startDate, endDate, selectedWeekdays]);
  const [chartType, setChartType] = useState<"line" | "pie">("line");

  return (
    <div className="admin-members-occupancy-module">
      <AdminSpecificMemberModule
        weekdayCounts={weekdayCounts}
        totalDays={totalDays}
        memberOccupancyFilterStore={memberOccupancyFilterStore}
        startDate={startDate}
        endDate={endDate}
        toggleSpecificFetch={toggleSpecificFetch}
        user={user}
        setShowChooseUserPopup={setShowChooseUserPopup}
      />

      <StatisticsFilterList
        useStore={memberOccupancyFilterStore}
        useTimeintervals={true}
        usePermissions={true}
        useGender={true}
        useTypeOfBookings={true}
        useTopListFilter={true}
        useAgeGroups={true}
      />
      <ReportTableSection
        title="Medlemmar - lista"
        columns={[
          { key: "name", label: "Namn" },
          { key: "memberType", label: "Medlemstyp" },
          { key: "timespan", label: "Tidsperiod", align: "left" },
          {
            key: "monday",
            label: `Måndag (${weekdayCounts.monday})`,
            align: "left",
          },
          {
            key: "tuesday",
            label: `Tisdag (${weekdayCounts.tuesday})`,
            align: "left",
          },
          {
            key: "wednesday",
            label: `Onsdag (${weekdayCounts.wednesday})`,
            align: "left",
          },
          {
            key: "thursday",
            label: `Torsdag (${weekdayCounts.thursday})`,
            align: "left",
          },
          {
            key: "friday",
            label: `Fredag (${weekdayCounts.friday})`,
            align: "left",
          },
          {
            key: "saturday",
            label: `Lördag (${weekdayCounts.saturday})`,
            align: "left",
          },
          {
            key: "sunday",
            label: `Söndag (${weekdayCounts.sunday})`,
            align: "left",
          },
          {
            key: "total",
            label: `Total (${totalDays})`,
            align: "left",
          },
        ]}
        rows={memberOccupancyData && memberOccupancyData.data}
        useCalcType={false}
        useToggleType={false}
        onToggleType={(isPie: boolean) => setChartType(isPie ? "pie" : "line")}
        useStore={memberOccupancyFilterStore}
        isLoading={false}
      />
      <div className="occupancy-spacer"></div>
    </div>
  );
};
export default AdminMembersOccupancyModule;
